/**
 * AffaireContextSidebar
 * Displays essential affaire details when navigating from Tracker to Facturation
 */

import React from 'react';
import {
  ClipboardDocumentIcon,
  MapPinIcon,
  CurrencyEuroIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAffaireContext } from '../../hooks/useAffaireContext';
import type { PaymentStatusType } from '../../api/facturation.api';
import './AffaireContextSidebar.styles.css';

interface AffaireContextSidebarProps {
  affaireId: number;
  isOpen?: boolean;
  onClose?: () => void;
}

/**
 * Format currency for display
 */
const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null) {
    return '—';
  }
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Copy text to clipboard
 */
const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

/**
 * Payment status configuration
 */
const getPaymentStatusConfig = (status: PaymentStatusType): { label: string; className: string } => {
  const config: Record<PaymentStatusType, { label: string; className: string }> = {
    'no_products': { label: '', className: '' },
    'a_facturer': { label: 'A facturer', className: 'fact-ctx-payment-badge--facturer' },
    'a_facturer_partiel': { label: 'A facturer (partiel)', className: 'fact-ctx-payment-badge--facturer' },
    'en_attente': { label: 'En attente', className: 'fact-ctx-payment-badge--attente' },
    'paye_partiel': { label: 'Partiel', className: 'fact-ctx-payment-badge--partiel' },
    'paye': { label: 'Paye', className: 'fact-ctx-payment-badge--paye' },
  };
  return config[status] || { label: '', className: '' };
};

export const AffaireContextSidebar: React.FC<AffaireContextSidebarProps> = ({
  affaireId,
  isOpen = true,
  onClose,
}) => {
  const { affaire, paymentSummary, isLoading } = useAffaireContext(affaireId);

  // Build full address string
  const fullAddress = affaire?.address
    ? [
        affaire.address.number,
        affaire.address.street,
        affaire.address.postcode,
        affaire.address.locality,
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  if (isLoading) {
    return (
      <>
        {isOpen && (
          <div className="fact-ctx-sidebar-overlay" onClick={onClose} aria-hidden="true" />
        )}
        <aside className={`fact-ctx-sidebar ${isOpen ? 'fact-ctx-sidebar--open' : ''}`}>
          <div className="fact-ctx-sidebar-header">
            <span className="fact-ctx-sidebar-title">Contexte Affaire</span>
            {onClose && (
              <button className="fact-ctx-close-btn" onClick={onClose} aria-label="Fermer">
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
          <div className="fact-ctx-sidebar-content">
            <div className="fact-ctx-loading">Chargement...</div>
          </div>
        </aside>
      </>
    );
  }

  if (!affaire) {
    return null;
  }

  const paymentStatus = paymentSummary?.payments?.paymentStatus;
  const paymentConfig = paymentStatus ? getPaymentStatusConfig(paymentStatus) : null;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fact-ctx-sidebar-overlay" onClick={onClose} aria-hidden="true" />
      )}

      {/* Sidebar */}
      <aside className={`fact-ctx-sidebar ${isOpen ? 'fact-ctx-sidebar--open' : ''}`}>
        {/* Header with close button (mobile) */}
        <div className="fact-ctx-sidebar-header">
          <span className="fact-ctx-sidebar-title">Contexte Affaire</span>
          {onClose && (
            <button className="fact-ctx-close-btn" onClick={onClose} aria-label="Fermer">
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="fact-ctx-sidebar-content">
          {/* Affaire ID */}
          <div className="fact-ctx-card">
            <div className="fact-ctx-card-header">
              <span className="fact-ctx-card-title">Affaire</span>
              <button
                className="fact-ctx-copy-btn"
                onClick={() => copyToClipboard(String(affaireId))}
                title="Copier le numero"
              >
                <ClipboardDocumentIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="fact-ctx-affaire-id">{affaireId}</div>
          </div>

          {/* Status */}
          <div className="fact-ctx-card">
            <div className="fact-ctx-card-header">
              <span className="fact-ctx-card-title">Statut</span>
            </div>
            <div className="fact-ctx-status-badge">{affaire.statut_affaire || 'N/A'}</div>
          </div>

          {/* Amount */}
          <div className="fact-ctx-card">
            <div className="fact-ctx-card-header">
              <CurrencyEuroIcon className="w-4 h-4 fact-ctx-card-icon" />
              <span className="fact-ctx-card-title">Montant TVAC</span>
            </div>
            <div className="fact-ctx-amount">
              {formatCurrency(affaire.montant_tvac_affaire)}
            </div>
          </div>

          {/* Address */}
          {fullAddress && (
            <div className="fact-ctx-card">
              <div className="fact-ctx-card-header">
                <MapPinIcon className="w-4 h-4 fact-ctx-card-icon" />
                <span className="fact-ctx-card-title">Bien</span>
                <button
                  className="fact-ctx-copy-btn"
                  onClick={() => copyToClipboard(fullAddress)}
                  title="Copier l'adresse"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="fact-ctx-address">
                {affaire.address?.number && affaire.address?.street && (
                  <div className="fact-ctx-address-street">
                    {affaire.address.number} {affaire.address.street}
                  </div>
                )}
                {(affaire.address?.postcode || affaire.address?.locality) && (
                  <div className="fact-ctx-address-city">
                    {affaire.address.postcode} {affaire.address.locality}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Expert */}
          {affaire.expert && (
            <div className="fact-ctx-card">
              <div className="fact-ctx-card-header">
                <UserIcon className="w-4 h-4 fact-ctx-card-icon" />
                <span className="fact-ctx-card-title">Expert</span>
              </div>
              <div className="fact-ctx-expert">{affaire.expert}</div>
            </div>
          )}

          {/* Payment Status */}
          {paymentConfig && paymentConfig.label && paymentStatus !== 'no_products' && (
            <div className="fact-ctx-card">
              <div className="fact-ctx-card-header">
                <span className="fact-ctx-card-title">Paiement</span>
              </div>
              <div className={`fact-ctx-payment-badge ${paymentConfig.className}`}>
                {paymentConfig.label}
                {paymentStatus === 'paye_partiel' && paymentSummary?.payments && (
                  <span className="fact-ctx-payment-amounts">
                    {' '}({formatCurrency(paymentSummary.payments.totalPaid)} / {formatCurrency(paymentSummary.invoicing?.totalInvoicedTvac)})
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default AffaireContextSidebar;
